import { Table } from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  pageSizeOptions?: number[];
  /** Total item count for server-side pagination (overrides client-side row count) */
  totalItems?: number;
  /** Whether to show the page size selector. Defaults to true. */
  showPageSizeSelector?: boolean;
}

/**
 * Generates an array of page numbers to display, with ellipsis markers.
 * Matches the existing Pagination.tsx logic: first 3, last 3, window around current.
 */
function getPageNumbers(
  currentPage: number,
  totalPages: number
): (number | 'ellipsis-start' | 'ellipsis-end')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  let firstPages = [0, 1, 2];
  let lastPages = [totalPages - 3, totalPages - 2, totalPages - 1];
  const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];

  if (currentPage > 2 && currentPage < totalPages - 3) {
    const between = [currentPage - 1, currentPage, currentPage + 1];
    const betweenInFirst = between.some(p => firstPages.includes(p));
    const betweenInLast = between.some(p => lastPages.includes(p));

    if (!betweenInFirst && !betweenInLast) {
      firstPages = [0, 1];
      lastPages = [totalPages - 2, totalPages - 1];
    }

    pages.push(...firstPages);

    if (!betweenInFirst) {
      pages.push('ellipsis-start');
    }

    pages.push(...between.filter(p => !firstPages.includes(p) && !lastPages.includes(p)));

    if (!betweenInLast) {
      pages.push('ellipsis-end');
    }
  } else {
    pages.push(...firstPages);
    pages.push('ellipsis-start');
  }

  pages.push(...lastPages);

  return pages;
}

/**
 * Computes the hidden pages for an ellipsis gap.
 * Given the visible pages array and the index of the ellipsis,
 * returns all page numbers between the neighbors.
 */
function getHiddenPages(
  pages: (number | 'ellipsis-start' | 'ellipsis-end')[],
  ellipsisIndex: number,
  totalPages: number
): number[] {
  // Find the last number before this ellipsis
  let startPage = 0;
  for (let i = ellipsisIndex - 1; i >= 0; i--) {
    if (typeof pages[i] === 'number') {
      startPage = pages[i] as number;
      break;
    }
  }

  // Find the first number after this ellipsis
  let endPage = totalPages - 1;
  for (let i = ellipsisIndex + 1; i < pages.length; i++) {
    if (typeof pages[i] === 'number') {
      endPage = pages[i] as number;
      break;
    }
  }

  // Return all pages between (exclusive of the neighbors since they're already visible)
  const hidden: number[] = [];
  for (let p = startPage + 1; p < endPage; p++) {
    hidden.push(p);
  }
  return hidden;
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 20, 50, 100],
  totalItems,
  showPageSizeSelector = true,
}: DataTablePaginationProps<TData>) {
  const currentPage = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const pageSize = table.getState().pagination.pageSize;
  const totalRows = totalItems ?? table.getFilteredRowModel().rows.length;

  const firstItemNumber = currentPage * pageSize + 1;
  const lastItemNumber = Math.min((currentPage + 1) * pageSize, totalRows);

  const pageNumbers = React.useMemo(
    () => getPageNumbers(currentPage, pageCount),
    [currentPage, pageCount]
  );

  const [pageSizeOpen, setPageSizeOpen] = React.useState(false);

  const isFirstPage = !table.getCanPreviousPage();
  const isLastPage = !table.getCanNextPage();

  return (
    <div className="flex items-center justify-between border-t border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 px-4 py-3 sm:px-4 sticky bottom-0 z-10">
      {/* Mobile: Previous / Next */}
      <div className="flex flex-1 justify-between sm:hidden">
        <span
          onClick={() => !isFirstPage && table.previousPage()}
          className={cn(
            'relative inline-flex items-center rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-600 dark:text-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50',
            isFirstPage && 'opacity-50 cursor-not-allowed'
          )}
        >
          Previous
        </span>
        <span
          onClick={() => !isLastPage && table.nextPage()}
          className={cn(
            'relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-600 dark:text-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50',
            isLastPage && 'opacity-50 cursor-not-allowed'
          )}
        >
          Next
        </span>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        {/* Left: Showing X to Y of Z results + rows per page */}
        <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-dark-gray-300">
          <span>Showing</span>
          <span className="font-medium">{firstItemNumber}</span>
          <span>to</span>
          <span className="font-medium">{lastItemNumber}</span>
          <span>of</span>
          <span className="font-medium">{totalRows}</span>
          <span>results</span>
          {showPageSizeSelector && (
            <>
              <span className="mx-1 text-gray-300 dark:text-dark-gray-600">|</span>
              <Popover open={pageSizeOpen} onOpenChange={setPageSizeOpen}>
                <PopoverTrigger asChild>
                  <button className="font-medium border-b border-dotted border-gray-500 dark:border-dark-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors">
                    {pageSize}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto min-w-[100px] p-1" sideOffset={8}>
                  <div className="flex flex-col">
                    {pageSizeOptions.map(size => (
                      <button
                        key={size}
                        onClick={() => {
                          table.setPageSize(size);
                          setPageSizeOpen(false);
                        }}
                        className={cn(
                          'px-3 py-1.5 text-sm text-left rounded hover:bg-gray-100 dark:hover:bg-dark-gray-500 transition-colors',
                          pageSize === size
                            ? 'text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20'
                            : 'text-gray-700 dark:text-dark-gray-300'
                        )}
                      >
                        {size} rows
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <span>per page</span>
            </>
          )}
        </div>

        {/* Right: Connected button strip pagination */}
        <nav
          className="isolate inline-flex -space-x-px rounded-md shadow-sm"
          aria-label="Pagination"
        >
          {/* Previous chevron */}
          <span
            onClick={() => !isFirstPage && table.previousPage()}
            className={cn(
              'relative inline-flex items-center rounded-l-md px-2 py-1.5',
              isFirstPage
                ? 'text-gray-300 dark:text-dark-gray-600 ring-1 ring-inset ring-gray-200 dark:ring-dark-gray-400 cursor-not-allowed'
                : 'text-gray-400 dark:text-dark-gray-400 ring-1 ring-inset ring-gray-200 dark:ring-dark-gray-400 hover:bg-gray-50 dark:hover:bg-dark-gray-700 cursor-pointer transition-colors duration-150'
            )}
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
          </span>

          {/* Page numbers */}
          {pageNumbers.map((page, idx) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              const hiddenPages = getHiddenPages(pageNumbers, idx, pageCount);

              if (hiddenPages.length === 0) return null;

              return (
                <Popover key={page}>
                  <PopoverTrigger asChild>
                    <span className="relative inline-flex items-center px-3 py-1.5 text-sm font-normal text-gray-900 dark:text-white ring-1 ring-inset ring-gray-200 dark:ring-dark-gray-400 hover:bg-gray-50 dark:hover:bg-dark-gray-700 cursor-pointer select-none transition-colors duration-150">
                      &hellip;
                    </span>
                  </PopoverTrigger>
                  <PopoverContent align="center" className="w-auto p-2" sideOffset={8}>
                    <div
                      className="grid gap-1 max-h-56 overflow-y-auto"
                      style={{
                        gridTemplateColumns: `repeat(${Math.min(hiddenPages.length, 5)}, minmax(0, 1fr))`,
                      }}
                    >
                      {hiddenPages.map(p => (
                        <button
                          key={p}
                          onClick={() => table.setPageIndex(p)}
                          className={cn(
                            'inline-flex items-center justify-center h-8 w-8 rounded text-sm transition-colors',
                            p === currentPage
                              ? 'bg-primary-600 text-white font-semibold'
                              : 'text-gray-700 dark:text-dark-gray-300 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-400'
                          )}
                        >
                          {p + 1}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }

            const isActive = page === currentPage;
            return (
              <span
                key={page}
                onClick={() => table.setPageIndex(page)}
                className={cn(
                  isActive
                    ? 'relative z-10 inline-flex items-center bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 cursor-default'
                    : 'relative inline-flex items-center px-3 py-1.5 text-sm font-normal text-gray-900 dark:text-white ring-1 ring-inset ring-gray-200 dark:ring-dark-gray-400 hover:bg-gray-50 dark:hover:bg-dark-gray-700 focus:z-20 focus:outline-offset-0 cursor-pointer transition-colors duration-150'
                )}
              >
                {(page as number) + 1}
              </span>
            );
          })}

          {/* Next chevron */}
          <span
            onClick={() => !isLastPage && table.nextPage()}
            className={cn(
              'relative inline-flex items-center rounded-r-md px-2 py-1.5',
              isLastPage
                ? 'text-gray-300 dark:text-dark-gray-600 ring-1 ring-inset ring-gray-200 dark:ring-dark-gray-400 cursor-not-allowed'
                : 'text-gray-400 dark:text-dark-gray-400 ring-1 ring-inset ring-gray-200 dark:ring-dark-gray-400 hover:bg-gray-50 dark:hover:bg-dark-gray-700 cursor-pointer transition-colors duration-150'
            )}
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        </nav>
      </div>
    </div>
  );
}
